import Entypo from '@expo/vector-icons/Entypo';
import {
  responsiveHeight,
  responsiveWidth,
  useResponsiveFontSize,
} from '@/components/Util/reactResponsiveDimensions';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import colors from '@/config/colors';
import TextGapWritingChoiceButton from '../Writing/Texts/GapWriting/TextGapWritingChoiceButton';
import HiddenText from '../Writing/WritingComponents/HiddenText';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { IS_TABLET, IS_WEB } from '@/utils/platform';
import { logError } from '@/utils/logHandling';

/**
 * Shuffles array in place.
 * @param {Array} a items The array containing the items.
 */
function shuffle(a: any[]) {
  let j, x, i;
  for (i = a.length; i; i--) {
    j = Math.floor(Math.random() * i);
    x = a[i - 1];
    a[i - 1] = a[j];
    a[j] = x;
  }
}

// get n random elements from an array
function getRandom(arr: any[], n: number) {
  let result = new Array(n),
    len = arr.length,
    taken = new Array(len);
  if (n > len) {
    throw new RangeError('getRandom: more elements taken than available');
  }
  while (n--) {
    let x = Math.floor(Math.random() * len);
    result[n] = arr[x in taken ? taken[x] : x];
    taken[x] = --len in taken ? taken[len] : len;
  }
  return result;
}

const DEFAULT_STATE = {
  currentGap: 0,
  _solved: false, // Responsible for rendering the bottom weiter button when all choices are finished
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  bodyContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.23,
    shadowRadius: 2.62,
    elevation: 4,
    backgroundColor: colors.white,
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: responsiveHeight(2),
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  scrollViewContentContainer: {
    paddingVertical: 30,
    paddingHorizontal: 40,
  },
  focused: {
    borderBottomColor: colors.darkPurpleBlue,
    borderBottomWidth: responsiveWidth(0.5),
  },
  solvedBorderBottomColor: {
    borderBottomColor: colors.black,
    borderBottomWidth: responsiveWidth(0.1),
  },
  notFocused: {
    borderBottomColor: colors.darkGrey,
    borderBottomWidth: responsiveWidth(0.5),
  },
  weiterContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  textContainerView: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
});

type ChoiceChunk = {
  word: string;
  markCorrect: boolean;
  markWrong: boolean;
};

type TextChunkObject = {
  type: string;
  word?: string;
  solved?: boolean;
  text?: string;
  choices?: ChoiceChunk[];
  index?: string;
};

function generateTextChunks(textContent: string) {
  let textChunks: string[] = [];
  let counter = 0;

  // split up text to array of text chunks. e.g. 'this [text] splits' => ['this','[text]','splits']
  textContent
    .split(/([\[\]])/)
    .filter(Boolean)
    .forEach(function (e) {
      const condition =
        e === '['
          ? counter++
          : e === ']'
            ? counter--
            : counter > 0
              ? textChunks.push(`[${e}]`)
              : textChunks.push(e);
      return (
        // Increase / decrease counter and push desired values to an array
        condition
      );
    });

  // create array of textChunkObjects that can be either of type 'gap' or of type 'tex†'
  const textChunkObjects: TextChunkObject[] = [];
  for (let i = 0; i < textChunks.length; i++) {
    if (textChunks[i][0] === '[') {
      const word = textChunks[i].replace(/[\[\]']+/g, '');
      textChunkObjects[i] = {
        type: 'gap',
        word,
        solved: false,
        index: `${i}-${word}`,
      };
      textChunkObjects[i].choices = [
        {
          word: textChunkObjects[i].word as string,
          markCorrect: false,
          markWrong: false,
        },
      ];
    } else {
      textChunkObjects[i] = {
        type: 'text',
        text: textChunks[i],
      };
    }
  }

  // for every gap add 3 random words from text
  for (let i = 0; i < textChunkObjects.length; i++) {
    if (textChunkObjects[i].type === 'gap') {
      /* jshint ignore:start */
      textChunkObjects[i].choices = textChunkObjects[i].choices?.concat(
        getRandom(
          textChunkObjects
            .filter(function (textChunk) {
              return (
                textChunk.type === 'gap' &&
                textChunk.word !== textChunkObjects[i].word
              );
            })
            .map(function (textChunk) {
              return {
                word: textChunk.word,
                markCorrect: false,
                markWrong: false,
                solved: false,
              };
            }),
          3,
        ),
      );
      shuffle(textChunkObjects[i].choices ?? []);
    }
  }
  return textChunkObjects;
}

let timerNextGap: null | ReturnType<typeof setTimeout> = null;

type GapProps = {
  textContent: any;
  skip: () => void;
  currentExercise: any;
  checkProgress: (
    skipped: boolean,
    goToNextSlide: boolean,
    isText: boolean,
  ) => void;
};

export function Gap({
  textContent,
  skip,
  currentExercise,
  checkProgress,
}: GapProps) {
  const [state, setState] = useState(DEFAULT_STATE);
  const [showOnlyTheCorrectAnswer, setShowOnlyTheCorrectAnswer] =
    useState<string>('');
  const [textChunks, setTextChunks] = useState<TextChunkObject[]>([]);
  const { currentGap, _solved } = state;
  const lastCalculatedFontSize = useRef<number | null>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const placeholderRefs = useRef<(View | null)[]>([]);
  const responsiveIconSize = useResponsiveFontSize(4);
  const responsiveFontSize = useResponsiveFontSize(3);

  const [dataSourceCords, setDataSourceCords] = useState<number[]>([]);
  const [init, setInit] = useState(false);
  const [isSolved, setSolved] = useState(0);
  const { bottom, left, right, top } = useSafeAreaInsets();
  const horizontalSafeAreaInsets = left + right;
  const verticalSafeAreaInsets = top + bottom;
  const choicesWrapperHeight = responsiveHeight(10);
  const choicesWrapperWidth = responsiveWidth(98) - horizontalSafeAreaInsets;
  let margin = responsiveWidth(2);

  const textContainerHeight = responsiveHeight(62) - verticalSafeAreaInsets;

  const nextExercise = function () {
    skip();
    checkProgress(false, true, true);
  };

  const responsiveStyles = StyleSheet.create({
    bodyContainer: {
      height: textContainerHeight,
    },
    buttonsContainer: {
      height: choicesWrapperHeight + responsiveHeight(3),
      width: '100%',
    },
    scrollViewContainer: {
      height: textContainerHeight,
    },
    responsiveText: {
      fontSize: responsiveFontSize,
      color: colors.primary,
      fontWeight: '700',
    },
    text: {
      fontSize: useResponsiveFontSize(3),
      lineHeight: useResponsiveFontSize(3) * 1.5,
      alignSelf: 'center',
      justifyContent: 'center',
      alignItems: 'center',
      verticalAlign: 'auto',
      alignContent: 'center',
    },
  });

  const textChunksGap = useMemo(() => {
    return textChunks.filter((textChunk) => {
      return textChunk.type == 'gap';
    });
  }, [textChunks]);

  const choiceFontSize = useMemo(() => {
    // if exercise is not solved yet, set font size for choice words
    if (textChunksGap.length == 0 || currentGap > textChunksGap.length - 1) {
      return 0;
    }
    if (!_solved) {
      const choices = textChunksGap[currentGap]?.choices
        ? textChunksGap[currentGap]?.choices.map((choice) => {
            return choice.word;
          })
        : [];
      // count the number of characters and words to calculate a good font size
      let choiceWordsLength = choices.length;
      let choicesWordsTotalCharacterCount = choices.join('').length;

      // calculate a good font size for the choice word buttons based on words count, character count, screen width and ratio
      let size =
        ((choicesWrapperWidth - choiceWordsLength * margin) /
          choicesWordsTotalCharacterCount) *
        1.6;

      // button font size can not be bigger then third the wrapper height
      size = Math.min(size, choicesWrapperHeight / 2);
      lastCalculatedFontSize.current = size;

      return size;
    } else {
      return lastCalculatedFontSize.current;
    }
  }, [choicesWrapperHeight, choicesWrapperWidth, textChunksGap, currentGap]);

  let wordMarginRight = {
    marginRight: responsiveStyles.text.fontSize / 3.5,
  };

  const textChunksElements = useMemo(() => {
    return textChunks.map((textChunk, index) => {
      if (textChunk.type == 'text') {
        return textChunk?.text?.split(' ').map((word, index2) => {
          return (
            <View key={index + '-' + index2} style={wordMarginRight}>
              <Text style={responsiveStyles.text}>{word.trim()}</Text>
            </View>
          );
        });
      } else {
        const { solved, word, index } = textChunk;
        const findWordIndex: number = textChunksGap.findIndex(
          (chunk) => chunk.word == word && chunk.index == index,
        );
        const isFocused = currentGap === findWordIndex;
        return (
          <View
            key={`${index}-${word}`}
            style={[
              isFocused
                ? styles.focused
                : currentGap > findWordIndex
                  ? styles.solvedBorderBottomColor
                  : styles.notFocused,
              wordMarginRight,
            ]}
            ref={(ref) => {
              if (placeholderRefs.current) {
                placeholderRefs.current[findWordIndex] = ref;
              }
            }}
            onLayout={(event) => {
              if (IS_TABLET) {
                const layout = event.nativeEvent.layout;
                if (layout.y !== undefined) {
                  setDataSourceCords((prev) => {
                    const next = [...prev];
                    next[findWordIndex] = layout.y;
                    return next;
                  });
                }
              }
            }}
          >
            <HiddenText
              style={[responsiveStyles.text]}
              text={word}
              visible={solved}
              isColored={isFocused ? solved : null}
              timeout={300}
              greenBackground={false}
            />
          </View>
        );
      }
    });
  }, [init, currentGap, isSolved]);

  const choicesElements = useMemo(() => {
    if (textChunksGap.length == 0) {
      return null;
    }
    return textChunksGap?.[currentGap]?.choices?.map((choice, index) => (
      <TextGapWritingChoiceButton
        key={`${currentGap}-${choice.word}-${index}`}
        solved={textChunksGap[currentGap].solved}
        choiceFontSize={choiceFontSize}
        word={choice.word}
        correctChoice={!('solved' in choice)}
        showOnlyTheCorrectAnswer={showOnlyTheCorrectAnswer}
        choiceSelected={() => choiceSelected(choice)}
      />
    ));
  }, [textChunksGap, currentGap, choiceFontSize, showOnlyTheCorrectAnswer]);

  const choiceSelected = (choice: ChoiceChunk) => {
    let nextFocusYCopy = dataSourceCords[currentGap + 1];
    setSolved(isSolved + 1);
    let currentTextChunk = textChunksGap[currentGap];
    if (!currentTextChunk.solved) {
      // if correct answer is selected, mark button green and go to next gap
      if (choice.word === currentTextChunk.word) {
        setShowOnlyTheCorrectAnswer(choice.word);
        const I = textChunks.findIndex(
          (textChunk) =>
            textChunk.word == currentTextChunk.word &&
            textChunk.index == currentTextChunk.index,
        );
        setTextChunks((prev) => {
          const updated = [...prev];
          updated[I] = { ...updated[I], solved: true };
          return updated;
        });
        timerNextGap = setTimeout(() => {
          setShowOnlyTheCorrectAnswer('');
          goToNextGap(nextFocusYCopy);
        }, 300);
      }
    }
  };

  const goToNextGap = (scrollToYCoordinate: number) => {
    if (!_solved) {
      if (currentGap < textChunksGap.length - 1) {
        scrollToActivePlaceholder(scrollToYCoordinate + 30);
      } else {
        if (scrollViewRef?.current && scrollViewRef?.current?.scrollToEnd) {
          scrollViewRef.current.scrollToEnd();
        }
      }
    }

    setState((prevState) => {
      return {
        ...prevState,
        currentGap: prevState.currentGap + 1,
      };
    });
  };

  const scrollToActivePlaceholder = (scrollToYCoordinate: number) => {
    try {
      const activePlaceholderIndex = textChunksGap.findIndex(
        (textChunk) => !textChunk.solved,
      );
      if (scrollViewRef?.current) {
        if (IS_WEB && activePlaceholderIndex !== -1) {
          const placeholderNode =
            placeholderRefs.current[activePlaceholderIndex];

          const domNode = placeholderNode as unknown as HTMLElement;
          if (domNode?.offsetTop !== undefined) {
            scrollViewRef.current.scrollTo({
              y: domNode.offsetTop + 150 - textContainerHeight / 2,
              animated: true,
            });
          }
        } else if (IS_TABLET) {
          // Code to scroll on Android and iOS
          scrollViewRef.current.scrollTo({
            y: scrollToYCoordinate - textContainerHeight / 2,
            animated: true,
          });
        }
      }
    } catch (error) {
      logError('Error scrolling to next word', error);
    }
  };

  useEffect(() => {
    const generatedTextChunks = generateTextChunks(textContent);
    setTextChunks(generatedTextChunks);
    setInit(false);
    if (scrollViewRef?.current) {
      scrollViewRef.current?.scrollTo({
        y: 0,
        animated: false,
      });
    }
  }, [textContent]);

  useEffect(() => {
    if (textChunksGap.length === 0) {
      return;
    }
    if (currentGap > textChunksGap.length - 1) {
      setState((prevState) => {
        return {
          ...prevState,
          _solved: true,
        };
      });
    }
  }, [currentGap, textChunksGap.length]);

  useEffect(() => {
    setState(DEFAULT_STATE);
    setInit(true);
    lastCalculatedFontSize.current = 0;
    return () => {
      if (timerNextGap) {
        clearTimeout(timerNextGap);
      }
    };
  }, [currentExercise]);

  return (
    <View style={styles.container}>
      <View style={[styles.bodyContainer, responsiveStyles.bodyContainer]}>
        <ScrollView
          contentContainerStyle={styles.scrollViewContentContainer}
          style={responsiveStyles.scrollViewContainer}
          ref={scrollViewRef}
        >
          <View style={styles.textContainerView}>{textChunksElements}</View>
        </ScrollView>
      </View>
      <View
        style={[styles.buttonsContainer, responsiveStyles.buttonsContainer]}
      >
        {_solved ? (
          <PressableNeolexon
            onPress={nextExercise}
            style={styles.weiterContainer}
          >
            <Text style={responsiveStyles.responsiveText}>weiter</Text>
            <Entypo
              name="arrow-bold-right"
              size={responsiveIconSize}
              color={colors.primary}
            />
          </PressableNeolexon>
        ) : (
          choicesElements
        )}
      </View>
    </View>
  );
}
